import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AdminService } from '../../../admin/admin.service';
import { Unit } from '../../../models/admin-models';
import { ReportingService } from '../../reporting.service';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { BridgeActionDetailModalComponent } from './bridge-action-detail-modal/bridge-action-detail-modal.component';
import { DateTime } from 'luxon';
import { GridComponent } from '@progress/kendo-angular-grid';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';

@Component({
  selector: 'app-bridge-action',
  templateUrl: './bridge-action.component.html',
  styleUrls: ['./bridge-action.component.scss'],
  standalone: false,
})
export class BridgeActionComponent implements OnInit {
  private admnSvc = inject(AdminService);
  private rptSvc = inject(ReportingService);
  private dialog = inject(MatDialog);

  unitsControl = new FormControl();
  units: Unit[] = [] as Unit[];
  filteredUnits = [];
  selectedUnit;
  bridgeActionData = { data: null, headers: null };
  bridgeActionDetailData = {};
  action = { unit: null, startDate: null, endDate: null };
  displayData = [];
  hiddenItemNum: number;
  loading: boolean;

  ngOnInit(): void {
    this.getUnits();
  }

  display(item) {
    return `${Object.keys(item)[0]}: ${item[Object.keys(item)[0]]}`;
  }

  setHiddenCols(item) {
    this.displayData.forEach(dataItem => {
      if (!dataItem[item.DataField] && dataItem[item.DataField] !== 0) {
        item.Hidden = true;
      }
    });
  }

  openDetailModal(ev, dataItem) {
    let detailModal = this.dialog.open(BridgeActionDetailModalComponent, {
      data: {
        dataItem: dataItem,
        startDate: this.action.startDate,
        endDate: this.action.endDate,
      },
      width: '80vw',
    });
    detailModal.afterClosed().subscribe(() => {
      detailModal = null;
    });
  }

  getBridgeAction() {
    this.loading = true;
    this.rptSvc
      .getBridgeAction(
        this.action.unit,
        this.action.startDate,
        this.action.endDate
      )
      .subscribe({
        next: data => {
          this.bridgeActionData = { ...data.results };
          this.displayData = this.bridgeActionData.data.filter(
            element => element.numActions > 0
          );
          this.bridgeActionData.headers.forEach(header => {
            this.setHiddenCols(header);
          });
          this.hiddenItemNum =
            this.bridgeActionData.data.length - this.displayData.length;

          this.displayData.forEach(dataItem => {
            if (
              dataItem['followUpsItems'] &&
              dataItem['followUpsItems'].length
            ) {
              dataItem['followUpsItems'] = dataItem['followUpsItems']
                .map(obj => {
                  const [key, value] = Object.entries(obj)[0];
                  return `${key}: ${value}`;
                })
                .join(',\n');
            } else if (
              !dataItem['followUpsItems'] ||
              dataItem['followUpsItems'].length === 0
            ) {
              dataItem['followUpsItems'] = 'None';
            }
            if (dataItem['iLikeItItems'] && dataItem['iLikeItItems'].length) {
              dataItem['iLikeItItems'] = dataItem['iLikeItItems']
                .map(obj => {
                  const [key, value] = Object.entries(obj)[0];
                  return `${key}: ${value}`;
                })
                .join(',\n');
            } else if (
              !dataItem['iLikeItItems'] ||
              dataItem['iLikeItItems'].length === 0
            ) {
              dataItem['iLikeItItems'] = 'None';
            }
            if (dataItem['noThanksItems'] && dataItem['noThanksItems'].length) {
              dataItem['noThanksItems'] = dataItem['noThanksItems']
                .map(obj => {
                  const [key, value] = Object.entries(obj)[0];
                  return `${key}: ${value}`;
                })
                .join(',\n');
            } else if (
              !dataItem['noThanksItems'] ||
              dataItem['noThanksItems'].length === 0
            ) {
              dataItem['noThanksItems'] = 'None';
            }
            if (dataItem['otherItems'] && dataItem['otherItems'].length) {
              dataItem['otherItems'] = dataItem['otherItems']
                .map(obj => {
                  const [key, value] = Object.entries(obj)[0];
                  return `${key}: ${value}`;
                })
                .join(',\n');
            } else if (
              !dataItem['otherItems'] ||
              dataItem['otherItems'].length === 0
            ) {
              dataItem['otherItems'] = 'None';
            }
          });
          this.loading = false;
        },
        error: error => {
          this.loading = false;
          console.error(error);
        },
      });
  }

  checkSelection() {
    if (
      !this.filteredUnits ||
      !this.filteredUnits.find(unit => {
        return unit.id === this.unitsControl.value;
      })
    ) {
      this.unitsControl.setValue(null);
    }
  }

  getUnits() {
    this.unitsControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value) {
          this.admnSvc.getUnitList(value, true).subscribe(resp => {
            if (resp['names']) {
              this.filteredUnits = resp['names'];
            }
          });
        }
      });
  }

  changeDate(ev) {
    if (ev.targetElement.className.includes('start-date')) {
      this.action.startDate = DateTime.fromJSDate(new Date(ev.value))
        .startOf('day')
        .toUTC()
        .toISO();
    } else if (ev.targetElement.className.includes('end-date')) {
      this.action.endDate = DateTime.fromJSDate(new Date(ev.value))
        .endOf('day')
        .toUTC()
        .toISO();
    }
  }

  exportToExcel(grid: GridComponent): void {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Disclosure',
        aiDisclosure: true,
        confirm: 'Agree and Download',
        cancel: 'Cancel/Exit',
        hideCancel: false,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        grid.saveAsExcel();
      }
      dialogRef = null;
    });
  }
}
